<script setup>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
    modelValue: {
        type: Array,
        default: () => []
    },
    clearable: {
        type: Boolean,
        default: true
    },
    placeholder: {
        type: String,
        default: ''
    },
    label: {
        type: String,
        default: ''
    },
    args: {
        type: Object,
        default: null
    }
});

const { t } = useI18n({ useScope: 'global' });

const emit = defineEmits(['update:modelValue']);

const emailInput = ref('');

const emails = ref([...(props.modelValue || [])]);

const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

const addEmail = () => {
    const input = emailInput.value.trim();
    if (!input) return;

    const emailsToAdd = input
        .split(',')
        .map((email) => email.trim())
        .filter((email) => email);

    emailsToAdd.forEach((email) => {
        if (isValidEmail(email) && !emails.value.includes(email)) {
            emails.value.push(email);
        }
    });

    emailInput.value = '';
    updateModelValue();
};

const removeEmail = (email) => {
    const idx = emails.value.indexOf(email);
    if (idx !== -1) {
        emails.value.splice(idx, 1);
        updateModelValue();
    }
};

const clearAll = () => {
    emails.value = [];
    updateModelValue();
};

const updateModelValue = () => {
    emit('update:modelValue', [...emails.value]);
};

watch(
    () => props.modelValue,
    (newValue) => {
        emails.value = [...(newValue || [])];
    },
    { deep: true }
);

const handleKeydown = (event) => {
    if (event.key === 'Enter' || event.key === ',') {
        event.preventDefault();
        addEmail();
    } else if (
        event.key === 'Backspace' &&
        !emailInput.value &&
        emails.value.length > 0
    ) {
        removeEmail(emails.value.length - 1);
    }
};

const isEmailValid = computed(() => {
    if (!emailInput.value.trim()) return true;
    return isValidEmail(emailInput.value.trim());
});
</script>

<template>
    <div class="email-filter">
        <v-label class="mb-2" v-if="props.label">{{ props.label }}</v-label>

        <div class="email-input-container" :class="{ error: !isEmailValid }">
            <div class="email-content">
                <div class="email-chips" v-if="emails.length > 0">
                    <v-chip
                        v-for="email in emails"
                        :key="email"
                        size="small"
                        :closable="!args.disableEmails"
                        color="primary"
                        variant="outlined"
                        class="email-chip"
                        @click:close="removeEmail(email)"
                    >
                        {{ email }}
                    </v-chip>
                </div>

                <v-text-field
                    v-model="emailInput"
                    :placeholder="props.placeholder"
                    variant="plain"
                    density="compact"
                    hide-details
                    @keydown="handleKeydown"
                    @blur="addEmail"
                    class="email-input"
                    :disabled="args.disableEmails"
                >
                </v-text-field>
            </div>

            <div
                class="email-actions"
                v-if="props.clearable && emails.length > 0"
            >
                <v-btn
                    icon="mdi-close"
                    size="small"
                    variant="text"
                    @click="clearAll"
                    :disabled="args.disableEmails"
                ></v-btn>
            </div>
        </div>

        <!-- Error message -->
        <div v-if="!isEmailValid" class="error-message text-caption mt-1">
            {{ t('Invalid email') }}
        </div>

        <!-- Hint text -->
        <div class="text-caption text-medium-emphasis mt-1">
            {{ t('Press Enter or comma to add. Use Backspace to remove.') }}
        </div>
    </div>
</template>

<style lang="scss" scoped>
.email-filter {
    .email-input-container {
        border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
        border-radius: 4px;
        min-height: 40px;
        display: flex;
        align-items: center;
        padding: 4px 8px;
        overflow: hidden;
        transition: border-color 0.2s;

        &:focus-within {
            border-color: rgb(var(--v-theme-primary));
            border-width: 2px;
        }

        &.error {
            border-color: rgb(var(--v-theme-error)) !important;
            border-width: 1px;
        }

        .email-content {
            flex: 1;
            display: flex;
            align-items: center;
            overflow-x: auto;
            overflow-y: hidden;
            min-width: 0;
            scrollbar-width: thin;
            scrollbar-color: rgba(var(--v-theme-primary), 0.3) transparent;

            &::-webkit-scrollbar {
                height: 4px;
            }

            &::-webkit-scrollbar-track {
                background: transparent;
            }

            &::-webkit-scrollbar-thumb {
                background: rgba(var(--v-theme-primary), 0.3);
                border-radius: 2px;
            }

            &::-webkit-scrollbar-thumb:hover {
                background: rgba(var(--v-theme-primary), 0.5);
            }
        }

        .email-chips {
            display: flex;
            align-items: center;
            gap: 4px;
            flex-shrink: 0;
            white-space: nowrap;

            .email-chip {
                font-size: 0.875rem;
                height: 28px;
                flex-shrink: 0;
                margin: 0 2px 0 0;
                white-space: nowrap;

                :deep(.v-chip__content) {
                    max-width: 200px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
            }
        }

        .email-input {
            flex: 1;
            min-width: 120px;
            flex-shrink: 0;
            height: 32px;

            :deep(.v-field) {
                border: none !important;
                box-shadow: none !important;
                padding: 0 !important;
                margin: 0 !important;
                height: 32px;

                .v-field__outline {
                    display: none;
                }

                .v-field__field {
                    padding: 0 !important;
                    margin: 0 !important;
                    height: 32px;
                    display: flex;
                    align-items: center;
                }

                .v-field__input {
                    padding: 0 !important;
                    margin: 0 !important;
                    min-height: unset !important;
                    height: 32px;
                    display: flex;
                    align-items: center;

                    input {
                        height: 32px !important;
                        line-height: 32px !important;
                        padding: 0 !important;
                        margin: 0 !important;
                    }
                }

                .v-field__append-inner {
                    display: none;
                }
            }
        }

        .email-actions {
            flex-shrink: 0;
            margin-left: 4px;
        }
    }

    .error-message {
        color: rgb(var(--v-theme-error));
        margin-top: 4px;
    }
}
</style>
